// @ts-nocheck
/**
 * sst-bundle-picker.js
 *
 * Per-item Color + Size dropdowns inside each pack row.
 *
 * Key design decisions:
 *
 * - NO document.currentScript dependency.
 *   Shopify section-rendering API re-injects external <script> tags in ways
 *   where currentScript is null, causing the old code to early-return and
 *   never initialise on soft-nav / section re-renders.
 *   All product data is now read from data-sst-variants / data-sst-options
 *   attributes on the <variant-picker> element itself.
 *
 * - window._sstBundlePickerLoaded guard ensures only ONE IIFE runs per page,
 *   even if Shopify re-executes the script tag multiple times.
 *
 * - Module-level state + single document-level submit interceptor so stale
 *   productFormComponent refs after Horizon section re-renders can't
 *   cause double-intercept or missed intercept.
 *
 * - N-pack (N > 1): submits ONE anchor line item at the total pack price.
 *   All item selections stored as visible line-item properties (Item 1 …).
 */
(function () {
  'use strict';

  if (window._sstBundlePickerLoaded) return;
  window._sstBundlePickerLoaded = true;

  // ── Module-level state — set/updated in initBundlePicker ─────────────────
  let VARIANTS     = [];
  let OPTION_NAMES = [];
  let SECTION_ID   = '';
  let COLOR_IDX    = -1;
  let SIZE_IDX     = -1;
  let PACK_IDX     = -1;
  let COLORS       = [];
  let SIZES        = [];

  let activeVariantPicker = null;
  let bundleActive        = false;
  let currentPackLabel    = null;
  // Tracks the pack the user explicitly clicked, set in capture phase BEFORE
  // Horizon's listener fires. Survives section re-renders because it's module-level.
  let userSelectedPack    = null;

  // ── Helpers ───────────────────────────────────────────────────────────────

  function findVariant(color, size, packValue) {
    return VARIANTS.find(
      (v) =>
        v.options[COLOR_IDX] === color &&
        v.options[SIZE_IDX]  === size  &&
        v.options[PACK_IDX]  === packValue
    ) || null;
  }

  function packCount(packLabel) {
    if (!packLabel) return 1;
    const n = parseInt(packLabel, 10);
    return isNaN(n) ? 1 : n;
  }

  function getProductFormComponent() {
    return document.querySelector(`product-form-component[data-section-id="${SECTION_ID}"]`);
  }

  function isOurProductForm(form) {
    return !!(
      form.closest(`product-form-component[data-section-id="${SECTION_ID}"]`) ||
      form.closest(`[data-section-id="${SECTION_ID}"]`)
    );
  }

  function getSelectedPackLabel() {
    if (!activeVariantPicker) return null;

    // 1. User's explicit click (captured before Horizon's listener runs) — survives
    //    any re-render because it lives at module level, not in the DOM.
    if (userSelectedPack) {
      const radio = activeVariantPicker.querySelector(
        `.variant-option--pack input[type="radio"][value="${CSS.escape(userSelectedPack)}"]`
      );
      if (radio) return userSelectedPack;
    }

    // 2. DOM checked state — reliable on hard-refresh / pre-selected variants.
    const checked =
      activeVariantPicker.querySelector('.variant-option--pack input[type="radio"]:checked') ||
      activeVariantPicker.querySelector('.variant-option--pack input[type="radio"][data-current-checked="true"]');
    return checked ? checked.value : null;
  }

  // Find the slot by the pack VALUE attribute — NOT by :checked state.
  // Horizon's connectedCallback can reset the checked property, but the value
  // attribute is static HTML and always findable.
  function getActiveSlot() {
    const packLabel = getSelectedPackLabel();
    if (!packLabel || !activeVariantPicker) return null;
    const radio = activeVariantPicker.querySelector(
      `.variant-option--pack input[type="radio"][value="${CSS.escape(packLabel)}"]`
    );
    return radio?.closest('.sst-pack-option')?.querySelector('.sst-bundle-rows-slot') || null;
  }

  function getErrorEl() {
    const s = getActiveSlot();
    return s ? s.querySelector('.sst-bundle-error') : null;
  }

  function showError(msg) {
    const el = getErrorEl();
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
    setTimeout(() => { el.hidden = true; }, 6000);
  }

  function clearError() {
    const el = getErrorEl();
    if (el) el.hidden = true;
  }

  function setButtonsDisabled(disabled) {
    const pfc = getProductFormComponent();
    if (!pfc) return;
    pfc.querySelectorAll('[name="add"], add-to-cart-component button[type="submit"]')
       .forEach((btn) => { btn.disabled = disabled; });
  }

  // ── Row HTML builder ──────────────────────────────────────────────────────

  function buildBundleRowsHTML(n) {
    const colorOptions = COLORS.map((c) => `<option value="${c}">${c}</option>`).join('');
    const sizeOptions  = SIZES.map((s)  => `<option value="${s}">${s}</option>`).join('');
    const caretSVG = `<svg aria-hidden="true" focusable="false" viewBox="0 0 10 6" width="10" height="6"><path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round"/></svg>`;

    let html = `<div class="sst-bundle-rows">`;
    for (let i = 1; i <= n; i++) {
      html += `
        <div class="sst-bundle-row" data-row="${i}">
          <span class="sst-bundle-row__num">#${i}</span>
          <div class="sst-bundle-row__selects">
            <div class="sst-bundle-select-wrap">
              <select class="sst-bundle-select sst-bundle-select--color" data-row="${i}" aria-label="Item ${i} color">
                <option value="">Color</option>
                ${colorOptions}
              </select>
              ${caretSVG}
            </div>
            <div class="sst-bundle-select-wrap">
              <select class="sst-bundle-select sst-bundle-select--size" data-row="${i}" aria-label="Item ${i} size">
                <option value="">Size</option>
                ${sizeOptions}
              </select>
              ${caretSVG}
            </div>
          </div>
        </div>`;
    }
    html += `<div class="sst-bundle-error" hidden></div>`;
    html += `</div>`;
    return html;
  }

  // ── Row update ────────────────────────────────────────────────────────────

  function updateRows() {
    if (!activeVariantPicker) return;

    activeVariantPicker.querySelectorAll('.sst-bundle-rows-slot').forEach((s) => {
      s.innerHTML = '';
      s.hidden = true;
    });

    currentPackLabel = getSelectedPackLabel();
    const slot = getActiveSlot();
    if (!slot) return;

    const n = packCount(currentPackLabel);
    bundleActive = true;
    slot.innerHTML = buildBundleRowsHTML(n);
    slot.hidden = false;
  }

  // ── Intercept pack radio changes — stop Horizon from processing them ─────
  // Horizon's variantChanged → selectedOptionsValues throws "No option value
  // ID found" when it encounters our pack fieldset, corrupting picker state
  // and causing our dropdowns to disappear. We own pack selection entirely.
  document.addEventListener('change', (e) => {
    if (e.target.closest('.sst-bundle-rows-slot')) return;
    const packInput = e.target.closest(
      'variant-picker[data-sst-variants] .variant-option--pack input[type="radio"]'
    );
    if (!packInput) return;

    // Store the user's choice (survives re-renders at module level)
    userSelectedPack = packInput.value;

    // Stop Horizon from seeing this change event — prevents its
    // variantChanged from firing, which would crash and corrupt DOM state.
    // stopImmediatePropagation also blocks same-element listeners (target phase).
    e.stopImmediatePropagation();

    // Update visual selected state on pack option labels manually
    const vp = packInput.closest('variant-picker[data-sst-variants]');
    if (vp) {
      vp.querySelectorAll('.sst-pack-option').forEach((label) => {
        label.classList.toggle('sst-pack-option--selected', label.contains(packInput));
      });
      // Update data-current-checked attributes
      vp.querySelectorAll('.variant-option--pack input[type="radio"]').forEach((r) => {
        r.dataset.currentChecked = (r === packInput) ? 'true' : 'false';
        r.dataset.previousChecked = 'false';
      });
    }

    // Trigger our own row update
    updateRows();
  }, true); // capture phase

  // ── Document-level submit interceptor (one, never stale) ─────────────────

  document.addEventListener('submit', function (event) {
    if (!bundleActive) return;
    if (!isOurProductForm(event.target)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    submitBundle();
  }, true);

  // ── Cart submission ───────────────────────────────────────────────────────

  async function submitBundle() {
    clearError();

    const packLabel = currentPackLabel;
    const n = packCount(packLabel);
    const activeSlot = getActiveSlot();
    const rows = Array.from(activeSlot ? activeSlot.querySelectorAll('.sst-bundle-row') : []);

    const rowData = [];
    for (let i = 0; i < rows.length; i++) {
      const row    = rows[i];
      const color  = row.querySelector('.sst-bundle-select--color')?.value || '';
      const size   = row.querySelector('.sst-bundle-select--size')?.value  || '';
      const rowNum = i + 1;

      if (!color) { showError(`Please select a color for item #${rowNum}.`); return; }
      if (!size)  { showError(`Please select a size for item #${rowNum}.`);  return; }

      const variant = findVariant(color, size, packLabel);
      if (!variant) {
        showError(`"${color} / ${size}" is not available for item #${rowNum}.`);
        return;
      }
      if (!variant.available) {
        showError(`"${color} / ${size}" is sold out for item #${rowNum}. Please choose another option.`);
        return;
      }
      rowData.push({ color, size, variant });
    }

    setButtonsDisabled(true);

    // 1-Pack: inject variant into Horizon's form and submit normally
    if (n === 1) {
      const pfc = getProductFormComponent();
      if (!pfc) { setButtonsDisabled(false); return; }
      const variantInput = pfc.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = rowData[0].variant.id;
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      setButtonsDisabled(false);
      bundleActive = false;
      pfc.querySelector('[name="add"], button[type="submit"]')?.click();
      bundleActive = true;
      return;
    }

    // N-Pack: single anchor line item at total pack price.
    // Submitting N items each at pack-variant price multiplies the total by N.
    const anchor = rowData[0].variant;

    const properties = { _bundle_pack: packLabel };
    rowData.forEach(({ color, size }, idx) => {
      properties[`Item ${idx + 1}`] = `${color} / ${size}`;
    });

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: anchor.id, quantity: 1, properties }],
          sections: sectionIds.join(','),
        }),
      });

      const data = await response.json();

      if (data.status) {
        showError(data.message || 'Could not add items to cart.');
        return;
      }

      const cartUpdateEvent = new Event('cart:update', { bubbles: true });
      cartUpdateEvent.detail = {
        resource: data,
        sourceId: getProductFormComponent()?.id || SECTION_ID,
        data: {
          source:    'product-form-component',
          itemCount: 1,
          productId: getProductFormComponent()?.dataset.productId,
          sections:  data.sections,
        },
      };
      document.dispatchEvent(cartUpdateEvent);
    } catch (err) {
      console.error('[sst-bundle-picker]', err);
      showError('Something went wrong. Please try again.');
    } finally {
      setButtonsDisabled(false);
    }
  }

  // ── Per-picker init ───────────────────────────────────────────────────────

  function initBundlePicker(vp) {
    // Read product data from picker element — works regardless of how the
    // script was loaded (true page load or Shopify section re-render inject).
    try {
      VARIANTS     = JSON.parse(vp.dataset.sstVariants || '[]');
      OPTION_NAMES = JSON.parse(vp.dataset.sstOptions  || '[]');
    } catch (e) {
      return;
    }
    const newSectionId = vp.dataset.sectionId || '';
    if (newSectionId !== SECTION_ID) userSelectedPack = null; // new product page
    SECTION_ID = newSectionId;

    COLOR_IDX = OPTION_NAMES.findIndex((n) => /colou?r/i.test(n));
    SIZE_IDX  = OPTION_NAMES.findIndex((n) => /size/i.test(n));
    PACK_IDX  = OPTION_NAMES.findIndex((n) => /pack/i.test(n));
    if (COLOR_IDX === -1 || SIZE_IDX === -1 || PACK_IDX === -1) return;

    COLORS = [...new Set(VARIANTS.map((v) => v.options[COLOR_IDX]))];
    SIZES  = [...new Set(VARIANTS.map((v) => v.options[SIZE_IDX]))];

    vp._sstBundleInit    = true;
    activeVariantPicker  = vp;

    if (!vp.querySelector('.variant-option--pack')) return;

    vp.addEventListener('change', (e) => {
      if (e.target.closest('.sst-bundle-rows-slot')) return;
      if (e.target.closest('.variant-option--pack')) updateRows();
    });

    updateRows();

    // Horizon's connectedCallback and its deferred operations may reset the
    // checked state after we first read it. Retry at short intervals to catch
    // any delayed state resolution.
    const vpRef = vp;
    [100, 300].forEach((delay) => {
      setTimeout(() => {
        if (activeVariantPicker !== vpRef) return; // picker replaced again, skip
        if (!getActiveSlot()?.querySelector('.sst-bundle-row')) updateRows();
      }, delay);
    });
  }

  // ── Find uninitialised pickers ────────────────────────────────────────────

  function init() {
    // Selector targets only pickers that have our data attributes (i.e. have a Pack option).
    document.querySelectorAll('variant-picker[data-sst-variants]').forEach((vp) => {
      if (vp._sstBundleInit) return;
      initBundlePicker(vp);
    });
  }

  // ── Watch body for Horizon replacing variant-picker after section re-render ─
  // Use setTimeout(init, 0) to defer until the next task, giving Horizon's
  // connectedCallback and any synchronous post-insert operations time to finish
  // before we query the pack radio's checked state.
  let _initTimer = null;
  new MutationObserver(() => {
    clearTimeout(_initTimer);
    _initTimer = setTimeout(init, 0);
  }).observe(document.body, { childList: true, subtree: true });

  // ── Bootstrap ─────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
